import React, { useEffect, useState } from 'react';

/**
 * EditorialSubNav, sticky in-page sub-navigation for Layer-2 editorial pages
 * (MICE, EVENTS, etc).
 *
 * Props:
 *   links: [{ label: string, target: string (DOM id) }]
 *   surface: 'navy' | 'bone', color treatment
 */
const EditorialSubNav = ({ links, surface = 'navy' }) => {
  const [active, setActive] = useState(links[0]?.target);

  useEffect(() => {
    const onScroll = () => {
      const offset = 140; // account for sticky height + breathing room
      let current = links[0]?.target;
      for (const l of links) {
        const el = document.getElementById(l.target);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) current = l.target;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [links]);

  const handleClick = (e, target) => {
    e.preventDefault();
    const el = document.getElementById(target);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const isNavy = surface === 'navy';
  const bg = isNavy ? '#0A132F' : '#F4EFE3';
  const baseColor = isNavy ? 'rgba(214,168,122,0.78)' : '#0B0F1F';
  const hairline = 'rgba(214,168,122,0.35)';

  return (
    <div
      className="sticky top-0 z-40 w-full"
      style={{
        backgroundColor: bg,
        borderBottom: `1px solid ${hairline}`,
        height: '56px',
      }}
      data-testid="editorial-subnav"
    >
      <div className="h-full max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <nav className="flex items-center overflow-x-auto whitespace-nowrap">
          {links.map((l, idx) => {
            const isActive = active === l.target;
            return (
              <React.Fragment key={l.target}>
                <a
                  href={`#${l.target}`}
                  onClick={(e) => handleClick(e, l.target)}
                  data-testid={`subnav-${l.target}`}
                  className="transition-colors"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '10pt',
                    fontWeight: 500,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: isActive ? '#D6A87A' : baseColor,
                    borderBottom: isActive
                      ? '1px solid #D6A87A'
                      : '1px solid transparent',
                    paddingBottom: '4px',
                  }}
                >
                  {l.label}
                </a>
                {idx < links.length - 1 && (
                  <span
                    className="mx-3 select-none"
                    aria-hidden="true"
                    style={{ color: hairline }}
                  >
                    ·
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default EditorialSubNav;
